import dayjs from "dayjs";
import { useMemo } from "react";
import * as ReactPlotly from "react-plotly.js";
import type { PlotParams } from "react-plotly.js";
import { LabellingCoverageBucket } from "store/api/generatedApi";

// Handle CJS interop - Vite 8/Rolldown double-wraps the default export
interface DefaultWrappedModule {
  default?: unknown;
}
const reactPlotlyModule = ReactPlotly as unknown as DefaultWrappedModule;
const reactPlotlyDefault = reactPlotlyModule.default as
  DefaultWrappedModule | undefined;
const Plot = (reactPlotlyDefault?.default ??
  reactPlotlyModule.default ??
  ReactPlotly) as React.ComponentType<PlotParams>;
import { trickleDownPasses } from "./coverageUtils";

interface Props {
  buckets: LabellingCoverageBucket[];
  height?: number | undefined;
  showLegend?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
}

export const CoverageStackedChart = ({
  buckets,
  height = 300,
  showLegend = true,
  showXAxis = true,
  showYAxis = true,
}: Props) => {
  const chartData = useMemo(() => {
    if (buckets.length === 0) {
      return {
        x: [],
        labellable: [],
        onePass: [],
        twoPass: [],
        threePlusPass: [],
        verified: [],
      };
    }

    const x = buckets.map((b) => dayjs(b.start).toISOString());

    const labellable: number[] = [];
    const onePass: number[] = [];
    const twoPass: number[] = [];
    const threePlusPass: number[] = [];
    const verified: number[] = [];

    buckets.forEach((bucket) => {
      const passes = bucket.labelled_by_passes;
      const trickledDownPasses = trickleDownPasses(passes);

      labellable.push(bucket.labellable_fraction);
      onePass.push(trickledDownPasses[1] ?? 0);
      twoPass.push(trickledDownPasses[2] ?? 0);
      threePlusPass.push(trickledDownPasses[3] ?? 0);
      verified.push(bucket.verified_fraction);
    });

    return { x, labellable, onePass, twoPass, threePlusPass, verified };
  }, [buckets]);

  const data = useMemo<Plotly.Data[]>(
    () => [
      // every track needs a bottom edge so fill works correctly
      {
        // verified bottom edge
        x: chartData.x,
        y: chartData.threePlusPass.map(() => 4),
        type: "scatter",
        mode: "none",
        fill: "tozeroy",
        fillcolor: "rgba(255, 255, 255, 0)",
        line: { shape: "hvh", width: 0 },
        showlegend: false,
        hoverinfo: "skip",
      },
      {
        name: "Verified",
        x: chartData.x,
        y: chartData.verified.map((v) => 4 + v),
        type: "scatter",
        mode: "none",
        fill: "tonexty",
        fillcolor: "rgba(34, 197, 94, 0.5)", // green, semi-transparent
        line: { shape: "hvh", width: 0 },
        hovertemplate: "Verified: %{customdata:.1%}<extra></extra>",
        customdata: chartData.verified,
        showlegend: true,
      },
      {
        // 3+ bottom edge
        x: chartData.x,
        y: chartData.threePlusPass.map(() => 3),
        type: "scatter",
        mode: "none",
        fill: "tozeroy",
        fillcolor: "rgba(255, 255, 255, 0)",
        line: { shape: "hvh", width: 0 },
        showlegend: false,
        hoverinfo: "skip",
      },
      {
        name: "3+ passes",
        x: chartData.x,
        y: chartData.threePlusPass.map((v) => 3 + v),
        type: "scatter",
        mode: "none",
        fill: "tonexty",
        fillcolor: "#a855f7", // purple
        line: { shape: "hvh", width: 0 },
        hovertemplate: "3+ passes: %{customdata:.1%}<extra></extra>",
        customdata: chartData.threePlusPass,
      },
      {
        // 2+ bottom edge
        x: chartData.x,
        y: chartData.twoPass.map(() => 2),
        type: "scatter",
        mode: "none",
        fill: "tozeroy",
        fillcolor: "rgba(255, 255, 255, 0)",
        line: { shape: "hvh", width: 0 },
        showlegend: false,
        hoverinfo: "skip",
      },
      {
        name: "2+ passes",
        x: chartData.x,
        y: chartData.twoPass.map((v) => 2 + v),
        type: "scatter",
        mode: "none",
        fill: "tonexty",
        fillcolor: "#f97316", // orange
        line: { shape: "hvh", width: 0 },
        hovertemplate: "2+ passes: %{customdata:.1%}<extra></extra>",
        customdata: chartData.twoPass,
      },
      {
        // 1+ bottom edge
        x: chartData.x,
        y: chartData.onePass.map(() => 1),
        type: "scatter",
        mode: "none",
        fill: "tozeroy",
        fillcolor: "rgba(255, 255, 255, 0)",
        line: { shape: "hvh", width: 0 },
        showlegend: false,
        hoverinfo: "skip",
      },
      {
        name: "1+ passes",
        x: chartData.x,
        y: chartData.onePass.map((v) => 1 + v),
        type: "scatter",
        mode: "none",
        fill: "tonexty",
        fillcolor: "#fbbf24", // yellow
        line: { shape: "hvh", width: 0 },
        hovertemplate: "1+ pass: %{customdata:.1%}<extra></extra>",
        customdata: chartData.onePass,
      },
      {
        name: "Labellable",
        x: chartData.x,
        y: chartData.labellable,
        type: "scatter",
        mode: "none",
        fill: "tozeroy",
        fillcolor: "#a3a3a3", // grey
        line: { shape: "hvh", width: 0 },
        hovertemplate: "Labellable: %{customdata:.1%}<extra></extra>",
        customdata: chartData.labellable,
      },
    ],
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- showLegend, showXAxis, showYAxis kept to recompute traces when axis visibility changes
    [chartData, showLegend, showXAxis, showYAxis]
  );

  const layout: Partial<Plotly.Layout> = {
    margin: {
      l: showYAxis ? 50 : 0,
      r: 0,
      t: 0,
      b: showXAxis ? 50 : 0,
    },
    showlegend: showLegend,
    legend: {
      orientation: "h",
      y: -0.2,
      x: 0.5,
      xanchor: "center",
    },
    xaxis: {
      visible: showXAxis,
      type: "date",
    },
    yaxis: {
      visible: showYAxis,
      range: [0, 4.5],
      fixedrange: true,
      tickmode: "array",
      tickvals: [0.5, 1.5, 2.5, 3.5],
      ticktext: ["Labellable", "1+", "2+", "3+"],
    },
    hovermode: "x unified",
    plot_bgcolor: "transparent",
    paper_bgcolor: "transparent",
    autosize: true,
  };

  layout.height = height;

  return (
    <Plot
      data={data}
      layout={layout}
      config={{
        displayModeBar: false,
        responsive: true,
      }}
      style={{ width: "100%", height: "100%" }}
    />
  );
};
